import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { abschlussPatchSchema, DEAL_STATUSES, type AbschlussPatch } from '../../../../shared/schemas/corrections'
// `PatchDealResult` (src/worker/services/corrections.ts) ist genau `{ deal: DealRow; recompute: RecomputeResult }`.
// Beide Formen stehen in src/worker/engine/types.ts — einer Datei ohne einen einzigen Import, also ohne D1-Typen;
// src/client/api.ts beschreibt die Antwort dieses PATCH heute nicht (`abschlussAendern` gibt dort `void` zurück).
// Ein zweites, handgeschriebenes `interface` wäre die Drift, gegen die Teil 25 gebaut ist.
import type { DealRow, RecomputeResult } from '../../../../worker/engine/types'
import type { AbschlussZeile, ProduktMitBegriffen, TeamZeile } from '../../../api'
import { api, istApiError } from '../../../api'
import { Betrag } from '../../../components/Betrag'
import { Dialog } from '../../../components/Dialog'
import { Fahne } from '../../../components/Fahne'
import { Feld } from '../../../components/Feld'
import { Info } from '../../../components/Info'
import { Knopf } from '../../../components/Knopf'
import { grundSchema, melde, pruefeKoerper, STATUS_TEXT } from '../../../components/korrekturen'
import { Leiste } from '../../../components/Leiste'
import { Tabelle } from '../../../components/Tabelle'
import { fehlermeldung, TabellenZustand, Zustand } from '../../../components/Zustand'
import { datum, eur, prozent } from '../../../format'
import { bpAusProzent, centsAusEingabe, eingabeAusCents, prozentAusBp } from '../../../geld'
import type { SeitenProps } from '../../../screen'

export interface AbschluesseSuche {
  status?: string
  produkt?: string
  q?: string
}

type Status = (typeof DEAL_STATUSES)[number]

/** So viele <th> stehen im Kopf der Tabelle — die Zustandszeilen darunter spannen genau darüber. */
const SPALTEN = 11

/** Die sechs Felder, die der Admin am Abschluss ändern darf. Alles andere gehört dem Webhook (§1.8 Schritt 1). */
interface BearbeitenAnsicht {
  closer_tc_id: string
  setter_tc_id: string
  host_tc_id: string
  sonstige_tc_id: string
  host_prozent: string
  overhead: string
  /** '' = nicht angegeben (zählt in den Regeln als selbst geholt), sonst intern | extern */
  lead_quelle: '' | 'intern' | 'extern'
  status: Status
  reason: string
}

const ansichtsSchema = z.object({
  closer_tc_id: z.string(),
  setter_tc_id: z.string(),
  host_tc_id: z.string(),
  sonstige_tc_id: z.string(),
  host_prozent: z.string(),
  overhead: z.string(),
  lead_quelle: z.enum(['', 'intern', 'extern']),
  status: z.enum(DEAL_STATUSES),
  reason: grundSchema,
})

/** Welches Körperfeld des Schemas an welchem Feld dieser Ansicht hängt; alles andere fällt auf `reason`. */
const KOERPERFELDER: Partial<Record<string, keyof BearbeitenAnsicht>> = {
  closer_tc_id: 'closer_tc_id',
  setter_tc_id: 'setter_tc_id',
  host_tc_id: 'host_tc_id',
  sonstige_tc_id: 'sonstige_tc_id',
  host_rate_bp: 'host_prozent',
  overhead_individual_cents: 'overhead',
  lead_source: 'lead_quelle',
  status: 'status',
}

/**
 * Ein Versionskonflikt (409 `version_konflikt`) ist der eine Fehler, nach dem der Dialog zugeht und die Liste
 * neu geholt wird — er wird deshalb am stabilen Code erkannt, nicht am deutschen Satz. Der Satz bleibt als
 * Rückfallweg, weil ein Fehler ohne Serverantwort (Netz weg) kein `ApiError` ist. Nicht am Status: 409 trägt
 * auch `tenant_beschaeftigt`, und dort ist Wiederholen im offenen Dialog genau das Richtige.
 */
function istVersionskonflikt(fehler: unknown): boolean {
  if (istApiError(fehler)) return fehler.error === 'version_konflikt'
  return fehler instanceof Error && fehler.message.includes('zwischenzeitlich geändert')
}

function prozentAusEingabe(text: string): number | null {
  const norm = text.trim().replace(',', '.')
  if (norm === '') return 0
  const wert = Number(norm)
  return Number.isFinite(wert) && wert >= 0 && wert <= 100 ? wert : null
}

interface Aenderung { feld: string; alt: string; neu: string }

const LEAD_TEXT: Readonly<Record<'' | 'intern' | 'extern', string>> = {
  '': 'nicht angegeben (zählt als intern)',
  extern: 'selbst geholt (extern)',
  intern: 'von der Firma gestellt (intern)',
}

/** Eine Person, wie der Dialog sie anbietet: mit dem Namen für den Probelauf und dem Hinweis, ob sie noch aktiv ist. */
interface Beteiligter { id: string; name: string; kuerzel: string; aktiv: boolean }

/**
 * Das aktive Team **plus** die Personen, die an diesem Abschluss hängen — auch stillgelegte. Ohne sie stünde
 * ein Rollenfeld auf einem Wert, den es nicht anzubieten hat: der Browser zeigt dann ein leeres Feld, obwohl
 * die Tabellenzeile daneben das Kürzel nennt, und der Probelauf müsste die rohe Kennung anzeigen.
 */
function beteiligtenAuswahl(team: TeamZeile[], deal: AbschlussZeile | null): Beteiligter[] {
  const zugewiesen = new Set(
    [deal?.closer_tc_id, deal?.setter_tc_id, deal?.host_tc_id, deal?.sonstige_tc_id].filter((id): id is string => typeof id === 'string'),
  )
  return team
    .filter((t) => t.active === 1 || zugewiesen.has(t.tenant_contractor_id))
    .map((t) => ({ id: t.tenant_contractor_id, name: t.display_name, kuerzel: t.short_code, aktiv: t.active === 1 }))
}

interface BearbeitenProps {
  deal: AbschlussZeile
  beteiligte: Beteiligter[]
  laeuft: boolean
  fehler: string | null
  onSchliessen: () => void
  onSpeichern: (koerper: AbschlussPatch) => void
}

function AbschlussBearbeitenDialog(p: BearbeitenProps): ReactNode {
  const { register, handleSubmit, watch, formState, setError } = useForm<BearbeitenAnsicht>({
    resolver: zodResolver(ansichtsSchema),
    defaultValues: {
      closer_tc_id: p.deal.closer_tc_id ?? '',
      setter_tc_id: p.deal.setter_tc_id ?? '',
      host_tc_id: p.deal.host_tc_id ?? '',
      sonstige_tc_id: p.deal.sonstige_tc_id ?? '',
      host_prozent: String(prozentAusBp(p.deal.host_rate_bp)).replace('.', ','),
      overhead: eingabeAusCents(p.deal.overhead_individual_cents),
      lead_quelle: p.deal.lead_source ?? '',
      status: p.deal.status as Status,
      reason: '',
    },
  })

  const name = (id: string | null): string =>
    id === null || id === '' ? '— niemand —' : p.beteiligte.find((b) => b.id === id)?.name ?? id

  /** Dieselbe Auswahl in allen drei Rollenfeldern — einmal beschrieben, dreimal eingesetzt. */
  const rollenOptionen = (
    <>
      <option value="">— niemand —</option>
      {p.beteiligte.map((b) => (
        <option key={b.id} value={b.id}>{b.name} ({b.kuerzel}){b.aktiv ? '' : ' — inaktiv'}</option>
      ))}
    </>
  )

  const werte = watch()
  const hostBp = prozentAusEingabe(werte.host_prozent)
  const overheadCents = centsAusEingabe(werte.overhead)

  /** Der Probelauf: was sich ändert, in Worten, bevor irgendetwas geschickt wird. */
  const aenderungen: Aenderung[] = []
  if ((werte.closer_tc_id === '' ? null : werte.closer_tc_id) !== p.deal.closer_tc_id) {
    aenderungen.push({ feld: 'Closer', alt: name(p.deal.closer_tc_id), neu: name(werte.closer_tc_id) })
  }
  if ((werte.setter_tc_id === '' ? null : werte.setter_tc_id) !== p.deal.setter_tc_id) {
    aenderungen.push({ feld: 'Setter', alt: name(p.deal.setter_tc_id), neu: name(werte.setter_tc_id) })
  }
  if ((werte.host_tc_id === '' ? null : werte.host_tc_id) !== p.deal.host_tc_id) {
    aenderungen.push({ feld: 'Empfehlungsgeber', alt: name(p.deal.host_tc_id), neu: name(werte.host_tc_id) })
  }
  if ((werte.sonstige_tc_id === '' ? null : werte.sonstige_tc_id) !== p.deal.sonstige_tc_id) {
    aenderungen.push({ feld: 'Sonstige Rolle', alt: name(p.deal.sonstige_tc_id), neu: name(werte.sonstige_tc_id) })
  }
  if (hostBp !== null && bpAusProzent(hostBp) !== p.deal.host_rate_bp) {
    aenderungen.push({ feld: 'Empfehlungsgeber-Anteil', alt: prozent(p.deal.host_rate_bp), neu: prozent(bpAusProzent(hostBp)) })
  }
  if (overheadCents !== null && overheadCents !== p.deal.overhead_individual_cents) {
    aenderungen.push({ feld: 'Overhead', alt: eur(p.deal.overhead_individual_cents), neu: eur(overheadCents) })
  }
  if ((werte.lead_quelle === '' ? null : werte.lead_quelle) !== p.deal.lead_source) {
    aenderungen.push({ feld: 'Lead-Quelle', alt: LEAD_TEXT[p.deal.lead_source ?? ''], neu: LEAD_TEXT[werte.lead_quelle] })
  }
  if (werte.status !== p.deal.status) {
    aenderungen.push({ feld: 'Status', alt: STATUS_TEXT[p.deal.status as Status], neu: STATUS_TEXT[werte.status] })
  }

  return (
    <Dialog offen titel={`${p.deal.customer_name} bearbeiten`} onSchliessen={p.onSchliessen}>
      <form
        onSubmit={(ereignis) => {
          ereignis.preventDefault()
          void handleSubmit((eingaben) => {
            const bp = prozentAusEingabe(eingaben.host_prozent)
            if (bp === null) {
              setError('host_prozent', { message: 'Bitte einen Anteil zwischen 0 und 100 angeben.' })
              return
            }
            const cents = centsAusEingabe(eingaben.overhead)
            if (cents === null) {
              setError('overhead', { message: 'Bitte einen Betrag wie 150,00 angeben.' })
              return
            }
            // Nur die geänderten Felder wandern in den Körper: ein PATCH, der ein Feld mit seinem alten Wert
            // mitschickt, erzeugt eine Audit-Zeile über eine Änderung, die es nicht gab.
            const koerper: AbschlussPatch = { version: p.deal.version, reason: eingaben.reason }
            const alsId = (wert: string): string | null => (wert === '' ? null : wert)
            if (alsId(eingaben.closer_tc_id) !== p.deal.closer_tc_id) koerper.closer_tc_id = alsId(eingaben.closer_tc_id)
            if (alsId(eingaben.setter_tc_id) !== p.deal.setter_tc_id) koerper.setter_tc_id = alsId(eingaben.setter_tc_id)
            if (alsId(eingaben.host_tc_id) !== p.deal.host_tc_id) koerper.host_tc_id = alsId(eingaben.host_tc_id)
            if (alsId(eingaben.sonstige_tc_id) !== p.deal.sonstige_tc_id) koerper.sonstige_tc_id = alsId(eingaben.sonstige_tc_id)
            if (bpAusProzent(bp) !== p.deal.host_rate_bp) koerper.host_rate_bp = bpAusProzent(bp)
            if (cents !== p.deal.overhead_individual_cents) koerper.overhead_individual_cents = cents
            const leadQuelle = eingaben.lead_quelle === '' ? null : eingaben.lead_quelle
            if (leadQuelle !== p.deal.lead_source) koerper.lead_source = leadQuelle
            if (eingaben.status !== p.deal.status) koerper.status = eingaben.status

            // Der letzte Prüfschritt gegen genau das Schema, mit dem der Server ihn gleich prüft — hier fängt
            // es „Bitte mindestens ein Feld ändern." ab, ohne dass jemand die Regel ein zweites Mal aufschreibt.
            const beanstandung = pruefeKoerper(abschlussPatchSchema, koerper)
            if (beanstandung !== null) {
              melde(setError, KOERPERFELDER, 'reason', beanstandung)
              return
            }
            p.onSpeichern(koerper)
          })()
        }}
      >
        <Feld label="Closer" htmlFor="a-closer">
          <select id="a-closer" {...register('closer_tc_id')}>
            {rollenOptionen}
          </select>
        </Feld>
        <Feld label="Setter" htmlFor="a-setter">
          <select id="a-setter" {...register('setter_tc_id')}>
            {rollenOptionen}
          </select>
        </Feld>
        <Feld label="Empfehlungsgeber" htmlFor="a-host">
          <select id="a-host" {...register('host_tc_id')}>
            {rollenOptionen}
          </select>
        </Feld>
        <Feld label="Sonstige Rolle" htmlFor="a-sonstige" hinweis="Die vierte Rolle, wie sie in den Regeln benannt ist — etwa Host.">
          <select id="a-sonstige" {...register('sonstige_tc_id')}>
            {rollenOptionen}
          </select>
        </Feld>
        <Feld
          label="Empfehlungsgeber-Anteil (%)"
          htmlFor="a-hostprozent"
          fehler={formState.errors.host_prozent?.message}
          hinweis="Nur für Abschlüsse mit eigenem Satz; 0 bedeutet: es gilt die Regel, nicht 0 €."
        >
          <input id="a-hostprozent" type="text" inputMode="decimal" {...register('host_prozent')} />
        </Feld>
        <Feld label="Overhead individuell (€)" htmlFor="a-overhead" fehler={formState.errors.overhead?.message}>
          <input id="a-overhead" type="text" inputMode="decimal" {...register('overhead')} />
        </Feld>
        <Feld
          label="Lead-Quelle"
          htmlFor="a-lead"
          hinweis="Ohne Angabe gilt: intern, von der Firma gestellt. Extern (selbst geholt) wird ein Abschluss per FinSuites-Tag oder hier."
        >
          <select id="a-lead" {...register('lead_quelle')}>
            {(Object.keys(LEAD_TEXT) as Array<'' | 'intern' | 'extern'>).map((q) => <option key={q} value={q}>{LEAD_TEXT[q]}</option>)}
          </select>
        </Feld>
        <Feld label="Status" htmlFor="a-status">
          <select id="a-status" {...register('status')}>
            {DEAL_STATUSES.map((s) => <option key={s} value={s}>{STATUS_TEXT[s]}</option>)}
          </select>
        </Feld>

        <section data-testid="probelauf" aria-label="Was sich ändert">
          <h3>Was sich ändert</h3>
          {aenderungen.length === 0 ? (
            <p>Noch nichts geändert.</p>
          ) : (
            <ul>
              {aenderungen.map((a) => <li key={a.feld}>{a.feld}: {a.alt} → {a.neu}</li>)}
            </ul>
          )}
          <p className="hinweis">
            Welche Provisionszeilen daraus folgen, rechnet der Server — er meldet es nach dem Speichern zurück.
          </p>
        </section>

        <Feld label="Grund (fürs Protokoll)" htmlFor="a-grund" fehler={formState.errors.reason?.message}>
          <textarea id="a-grund" rows={2} {...register('reason')} />
        </Feld>

        {p.fehler === null ? null : <p role="alert" className="fehler">{p.fehler}</p>}
        <div className="knoepfe">
          <Knopf type="submit" variante="primaer" disabled={p.laeuft}>
            {p.laeuft ? 'Bitte warten …' : 'Änderung speichern'}
          </Knopf>
          <Knopf type="button" variante="still" onClick={p.onSchliessen} disabled={p.laeuft}>Abbrechen</Knopf>
        </div>
      </form>
    </Dialog>
  )
}

interface PatchAntwort { deal: DealRow; recompute: RecomputeResult }

export function AbschluesseSeite({ tenantId, suche, setzeSuche, gehZu: _gehZu }: SeitenProps<AbschluesseSuche>): ReactNode {
  const qc = useQueryClient()
  const [bearbeitet, setBearbeitet] = useState<AbschlussZeile | null>(null)
  const [meldung, setMeldung] = useState('')
  const [konflikt, setKonflikt] = useState<string | null>(null)
  const [suchtext, setSuchtext] = useState(suche.q ?? '')

  const liste = useQuery({
    queryKey: ['abschluesse', tenantId, suche.status ?? '', suche.produkt ?? '', suche.q ?? ''],
    queryFn: () => api.get<{ abschluesse: AbschlussZeile[] }>(`/api/t/${tenantId}/abschluesse`, {
      status: suche.status, produkt: suche.produkt, q: suche.q,
    }),
  })
  const team = useQuery({
    queryKey: ['team', tenantId],
    queryFn: () => api.get<{ team: TeamZeile[] }>(`/api/t/${tenantId}/team`),
  })
  const produkte = useQuery({
    queryKey: ['produkte', tenantId],
    queryFn: () => api.get<{ produkte: ProduktMitBegriffen[] }>(`/api/t/${tenantId}/produkte`),
  })

  /**
   * Abschlüsse, die kamen, bevor Team und Produkte standen, bleiben sonst für immer ohne Produkt und
   * Closer (Produkt und Rollen setzt eine Lieferung nur beim ersten Anlegen). Der Knopf liest ihre
   * FinSuites-Lieferung noch einmal und füllt, was leer ist — nie etwas, das schon gesetzt ist.
   */
  const ergaenzen = useMutation({
    mutationFn: () => api.post<{ ergaenzt: Array<{ id: string; customer_name: string; felder: string[] }>; offen: number }>(
      `/api/t/${encodeURIComponent(tenantId)}/abschluesse/ergaenzen`, {}),
    onSuccess: (antwort) => {
      const n = antwort.ergaenzt.length
      setMeldung(
        (n === 0 ? 'Nichts zu ergänzen.' : `${n} ${n === 1 ? 'Abschluss' : 'Abschlüsse'} ergänzt.`)
        + (antwort.offen === 0 ? '' : ` ${antwort.offen} ${antwort.offen === 1 ? 'hat' : 'haben'} weiter Lücken — meist fehlt die Person im Team (Einstellungen → Team, „Name in FinSuites“) oder ein Name unter „Auch bekannt als“ beim Produkt.`),
      )
      for (const schl of ['abschluesse', 'zahlungen', 'provisionen', 'inbox', 'dashboard']) {
        void qc.invalidateQueries({ queryKey: [schl, tenantId] })
      }
    },
  })
  const mitLuecke = (liste.data?.abschluesse ?? []).filter((d) => d.product_id === null || d.closer_tc_id === null).length

  const speichern = useMutation<PatchAntwort, Error, { dealId: string; koerper: AbschlussPatch }>({
    mutationFn: ({ dealId, koerper }) => api.patch<PatchAntwort>(`/api/t/${tenantId}/abschluesse/${dealId}`, koerper),
    onSuccess: (antwort) => {
      const r = antwort.recompute
      setMeldung(
        `Gespeichert. ${r.commissionsInserted} Provisionszeilen neu, ${r.commissionsVoided} storniert, ${r.reversals} zurückgebucht.`,
      )
      setKonflikt(null)
      setBearbeitet(null)
      for (const schl of ['abschluesse', 'provisionen', 'inbox', 'dashboard', 'zahlungen']) {
        void qc.invalidateQueries({ queryKey: [schl, tenantId] })
      }
    },
    onError: (fehler) => {
      // Ein Versionskonflikt ist keine Eingabekorrektur: jemand anderes hat den Abschluss inzwischen geändert.
      // Den Dialog offen zu lassen hieße, dem Bediener anzubieten, die fremde Änderung zu überschreiben.
      if (!istVersionskonflikt(fehler)) return
      setMeldung('')
      setKonflikt(fehler.message)
      setBearbeitet(null)
      void qc.invalidateQueries({ queryKey: ['abschluesse', tenantId] })
    },
  })

  const beteiligte = beteiligtenAuswahl(team.data?.team ?? [], bearbeitet)
  const dialogFehler = speichern.error !== null && !istVersionskonflikt(speichern.error)
    ? speichern.error.message
    : null

  return (
    <div className="seite">
      <h1>Abschlüsse</h1>
      <p role="status" aria-live="polite" className="meldung">{meldung}</p>
      {konflikt === null ? null : <p role="alert" className="fehler">{konflikt}</p>}
      {mitLuecke === 0 ? null : (
        <p className="aktionen">
          <span>{mitLuecke} {mitLuecke === 1 ? 'Abschluss ist' : 'Abschlüsse sind'} ohne Produkt oder Closer.</span>
          <Knopf disabled={ergaenzen.isPending} onClick={() => { setMeldung(''); ergaenzen.mutate() }}>
            Fehlendes aus FinSuites ergänzen
          </Knopf>
        </p>
      )}
      {ergaenzen.isError ? <p role="alert" className="fehler">{fehlermeldung(ergaenzen.error)}</p> : null}

      <Leiste beschriftung="Abschlüsse filtern">
        {/* „Statusfilter", nicht „Status": der Bearbeiten-Dialog trägt daneben ein Feld „Status", und zwei
            gleichnamige Beschriftungen auf einem Bildschirm sind für eine Vorlesesoftware nicht auseinanderzuhalten. */}
        <Feld label="Statusfilter" htmlFor="f-status">
          <select id="f-status" value={suche.status ?? ''} onChange={(e) => { setzeSuche({ status: e.target.value === '' ? undefined : e.target.value }) }}>
            <option value="">alle</option>
            {DEAL_STATUSES.map((s) => <option key={s} value={s}>{STATUS_TEXT[s]}</option>)}
          </select>
        </Feld>
        <Feld label="Produkt" htmlFor="f-produkt">
          <select id="f-produkt" value={suche.produkt ?? ''} onChange={(e) => { setzeSuche({ produkt: e.target.value === '' ? undefined : e.target.value }) }}>
            <option value="">alle</option>
            {(produkte.data?.produkte ?? []).map((pr) => <option key={pr.id} value={pr.code}>{pr.code} — {pr.name}</option>)}
          </select>
        </Feld>
        <Feld label="Suche" htmlFor="f-suche" info="Kundenname, FinSuites-Abschluss-ID oder Kontakt-ID.">
          <input id="f-suche" type="search" value={suchtext} onChange={(e) => { setSuchtext(e.target.value) }} />
        </Feld>
        <Knopf onClick={() => { setzeSuche({ q: suchtext === '' ? undefined : suchtext }) }}>Suchen</Knopf>
      </Leiste>

      {/* Die Filterleiste bleibt in jedem Zustand stehen: wer gerade gesucht hat, muss sehen, wonach —
          sonst bezieht sich „lädt" oder „nichts gefunden" auf nichts Sichtbares. Nur die Tabelle selbst
          weicht dem Lade- und dem Fehlerzustand, weil eine Tabelle ohne Zeilen keinen Kopf braucht. */}
      {liste.isPending ? <Zustand art="laedt" beschriftung="Die Abschlüsse werden geladen." /> : null}
      {liste.isError ? (
        <Zustand art="fehler" fehler={liste.error} onWiederholen={() => { void liste.refetch() }} />
      ) : null}
      {liste.data === undefined ? null : (
        <Tabelle
          beschriftung="Abschlüsse"
          kopf={
            <tr>
              <th scope="col">Kunde</th>
              <th scope="col">Produkt</th>
              <th scope="col">Abschluss am</th>
              <th scope="col" className="zahl">Vertragswert</th>
              <th scope="col">Closer</th>
              <th scope="col">Setter</th>
              <th scope="col">Empfehlungsgeber</th>
              <th scope="col">Sonstige</th>
              <th scope="col" className="zahl">Overhead</th>
              <th scope="col">Status</th>
              <th scope="col">Bearbeiten</th>
            </tr>
          }
        >
          {liste.data.abschluesse.length === 0 ? (
            <TabellenZustand
              art="leer" spalten={SPALTEN} titel="Noch keine Abschlüsse"
              hinweis="Abschlüsse kommen aus dem Deal-Webhook. Prüfen Sie unter Einstellungen → Webhook, ob die URL im FinSuites-Workflow eingetragen ist."
            />
          ) : null}
          {liste.data.abschluesse.map((d) => (
            <tr key={d.id}>
              <th scope="row">{d.customer_name}</th>
              <td className="mono">{d.product_code ?? '—'}</td>
              <td className="mono">{datum(d.signed_at)}</td>
              <td className="zahl">{d.contract_value_cents === null ? '—' : <Betrag cents={d.contract_value_cents} />}</td>
              <td className="mono">{d.closer_short_code ?? '—'}</td>
              <td className="mono">{d.setter_short_code ?? '—'}</td>
              <td className="mono">
                {/* Der Anteil des Empfehlungsgebers ist keine eigene Spalte wert (Bastian, 11.10.2026): er steht
                    hinter dem i, und nur dort, wo überhaupt jemand eingetragen ist. */}
                {d.host_short_code === null ? '—' : (
                  <span className="zelle-mit-info">
                    {d.host_short_code}
                    <Info titel={`Anteil von ${d.host_short_code}`}>
                      {d.host_rate_bp > 0
                        ? `Empfehlungsgeber-Anteil an diesem Abschluss: ${prozent(d.host_rate_bp).replace(' ', '\u00a0')}.`
                        : 'Kein eigener Anteil am Abschluss eingetragen — es gilt die Provisionsregel für Empfehlungsgeber.'}
                    </Info>
                  </span>
                )}
              </td>
              <td className="mono">{d.sonstige_short_code ?? '—'}</td>
              <td className="zahl"><Betrag cents={d.overhead_individual_cents} /></td>
              <td><Fahne art={d.status === 'aktiv' || d.status === 'weiterlauf' ? 'ok' : d.status === 'pruefen' ? 'warnung' : 'neutral'}>{STATUS_TEXT[d.status as Status]}</Fahne></td>
              <td>
                {/* Jeder Weg in den Dialog räumt zuerst auf: die Meldung der vorigen Änderung und ein alter
                    Konflikthinweis reden sonst über einen Bildschirm, den es so nicht mehr gibt. */}
                <Knopf variante="still" onClick={() => { setMeldung(''); setKonflikt(null); speichern.reset(); setBearbeitet(d) }}>
                  {d.customer_name} bearbeiten
                </Knopf>
              </td>
            </tr>
          ))}
        </Tabelle>
      )}

      {/* `key` ist hier kein Schmuck: die Vorbelegungen des Formulars gelten nur beim Einhängen. Ohne eigene
          Identität je Abschluss behielte ein schon offener Dialog beim Wechsel auf eine andere Zeile die Werte
          der ersten — und schriebe sie beim Speichern auf den zweiten Abschluss. */}
      {bearbeitet === null ? null : (
        <AbschlussBearbeitenDialog
          key={bearbeitet.id}
          deal={bearbeitet}
          beteiligte={beteiligte}
          laeuft={speichern.isPending}
          fehler={dialogFehler}
          onSchliessen={() => { speichern.reset(); setBearbeitet(null) }}
          onSpeichern={(koerper) => { speichern.mutate({ dealId: bearbeitet.id, koerper }) }}
        />
      )}
    </div>
  )
}

export const Route = createFileRoute('/t/$tenantId/abschluesse')({
  validateSearch: (roh: Record<string, unknown>): AbschluesseSuche => {
    const text = (schluessel: string): string | undefined => {
      const wert = roh[schluessel]
      return typeof wert === 'string' && wert !== '' ? wert : undefined
    }
    return { status: text('status'), produkt: text('produkt'), q: text('q') }
  },
  component: AbschluesseRoute,
})

function AbschluesseRoute(): ReactNode {
  const { tenantId } = Route.useParams()
  const suche = Route.useSearch()
  const navigate = useNavigate()
  return (
    <AbschluesseSeite
      tenantId={tenantId}
      suche={suche}
      setzeSuche={(naechste) => { void navigate({ to: '.', search: (alt: AbschluesseSuche) => ({ ...alt, ...naechste }) }) }}
      gehZu={(pfad) => { void navigate({ to: pfad }) }}
    />
  )
}
